/* eslint-disable no-new-wrappers */
import Link from 'next/link';
import PropTypes from 'prop-types';
import { decode } from 'html-entities';
import { useRouter } from 'next/router';
import { useCookies } from 'react-cookie';
import { useDebounce, useLocalStorage } from 'react-use';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

// Hooks
import useAuth from 'hooks/useAuth';
import useCart from 'hooks/useCart';
import useCurrency from 'hooks/useCurrency';
import useFeatureFlags from 'hooks/useFeatureFlags';
import useFollowing from 'hooks/useFollowing';
import useReferrals from 'hooks/useReferrals';
import useResponsive from 'hooks/useResponsive';
import useSignupPopup from 'hooks/useSignupPopup';

// Material UI
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardHeader from '@mui/material/CardHeader';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MuiLink from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import LocalOfferIcon from '@mui/icons-material/LocalOfferRounded';
import LocalPostOffice from '@mui/icons-material/LocalPostOfficeRounded';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import DateRangeIcon from '@mui/icons-material/DateRange';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PackageIcon from 'components/Icons/PackageIcon';

// Components
import Label from 'components/Label';
import QuantitySelector from 'components/QuantitySelector';
import ActionInfoButton from 'components/ActionInfoButton';
import MessageBrandDialog from 'components/Dialog/MessageBrandDialog/MessageBrandDialog';
import CustomTooltip from 'components/CustomTooltip';

/** Utils / Consts */
import { RESOLUTION } from 'constants/image-resolution';
import WP_ROUTES from 'constants/wordpress-routes';
import {
  addNoFollow,
  renderKycInfo,
  setPriceInDesiredCurrency,
  splitPrice,
  transformImageUrl,
  calculateLeadTimeText,
} from 'utils/functions';
import { GTMProductAddToCartEvents, pushAlgoliaAddToCartEvent } from 'utils/gtm';
import { isProductOutOfStock, validateStockAmount } from 'utils/stockValidation.utils';
import { USER_CREDIT_COOKIE_NAME } from 'constants/index';

const styles = {
  strike: {
    textDecoration: 'line-through',
    textDecorationStyle: 'solid' /* solid/double/dotted/dashed */,
    variant: 'body1',
    textDecorationColor: 'rgba(0,0,0,0.35)',
  },
  brandArea: {
    mt: '-0.25rem',
    mb: '-0.25rem',
    ml: '-1rem',
  },
};

const ACCORDION_PANELS = Object.freeze({
  SHIPPING: 'shipping',
  DESCRIPTION: 'description',
  VENDOR_INFO: 'vendor-info',
});

const BUTTON_TEXT_VALUES = Object.freeze({
  FOLLOW: 'Follow',
  UNFOLLOW: 'Unfollow',
  FOLLOWING: 'Following',
});

const productDetailInfoDefaultPropSx = {};

const ProductDetailInfo = ({ sx = productDetailInfoDefaultPropSx, product, loading, onVariationChange }) => {
  const theme = useTheme();
  const isTablet = useResponsive('down', 'md');
  const isMobile = useResponsive('down', 'sm');

  const [cookies] = useCookies();

  const leadTimeText = calculateLeadTimeText({
    roundedAverageLeadTime: product?.wholesaler?.roundedAverageLeadTime,
    shipsIn: product?.shipsIn,
  });

  const router = useRouter();
  const { isAuthenticated, userId, user } = useAuth();
  const { cart, addToCart, removeFromCart, increaseInCart, decreaseInCart } = useCart();
  const { currency, exchangeRates } = useCurrency();
  const [, setRedirectUrl] = useLocalStorage('redirect-url', '');

  const [currencySymbol, setCurrencySymbol] = useState(currency?.symbol || '£');

  const { handleShowSignupPopup } = useSignupPopup();
  const { following, follow, unfollow } = useFollowing();
  const [isFollowing, setIsFollowing] = useState(false);
  const [buttonText, setButtonText] = useState('Follow');
  const [messageBrandDialogOpen, setMessageBrandDialogOpen] = useState(false);

  // configs
  const { IS_WORDPRESS_CONNECTED, SEARCH_RESULTS_ALGOLIA: isAlgoliaEnabled } = useFeatureFlags();

  // Send Algolia Insights events only when if feature flag is enabled, redirected from search results
  const algoliaQueryParam = router?.query['query-id'];
  const hasAlgoliaQueryParam = !!algoliaQueryParam && typeof algoliaQueryParam === 'string';
  const shouldSendAlgoliaInsights = isAlgoliaEnabled;

  const productIdentifier = IS_WORDPRESS_CONNECTED ? product?.WP_id : product?.id;

  const [expanded, setExpanded] = useState(ACCORDION_PANELS.DESCRIPTION);
  const [canAddToCart, setCanAddToCart] = useState(false);

  const handlePanelChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const { queryParamsString } = useReferrals();

  const handleOnSignupClick = async () => {
    const currentPath = router.asPath;
    await setRedirectUrl(currentPath);
    const registerUrl = `${WP_ROUTES.register}?${queryParamsString}`;
    router.push(registerUrl);
  };

  // price data
  const [selectedWSP, setSelectedWSP] = useState(0);
  const [selectedRRP, setSelectedRRP] = useState(0);
  const [selectedMinOrderValue, setSelectedMinOrderValue] = useState(0);

  // Stock and pack data
  const minimumOrderQuantity = useMemo(
    () => Math.max(product?.productMinQuantity || 1, 1),
    [product?.productMinQuantity]
  );
  const maxStockAvailable = useMemo(
    () =>
      product?.productInventory?.productInventoryTracked === 'yes' ? product?.productInventory?.productStock || 0 : 0,
    [product?.productInventory?.productInventoryTracked, product?.productInventory?.productStock]
  );
  const productOrderQuantity = useMemo(
    () => Math.max(product?.productOrderQuantity || 1, 1),
    [product?.productOrderQuantity]
  );

  // cart data & handlers
  const [inCart, setInCart] = useState(false);
  const [cartItemId, setCartItemId] = useState(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isSelectingQuantity, setIsSelectingQuantity] = useState(false);
  const [shouldTriggerCartApi, setShouldTriggerCartApi] = useState(false);

  const [actualQuantity, setActualQuantity] = useState(null);
  const [previousQuantity, setPreviousQuantity] = useState(0);
  const [quantityToReset, setQuantityToReset] = useState(null);
  const [addToCartMinQuantity, setAddToCartMinQuantity] = useState(1);

  // variations data
  const [hasVariations, setHasVariations] = useState(false);
  const [variationsMetadata, setVariationsMetadata] = useState([]);
  const [selectedVariationOptions, setSelectedVariationOptions] = useState({});
  const [variationIdInDisplay, setVariationIdInDisplay] = useState(0);

  const [showVariationError, setShowVariationError] = useState(false);
  const [stockError, setStockError] = useState([]);

  const setProductInCartQuantities = (productInCart) => {
    validateStockAmount({
      product,
      cartItemId,
      hasVariations,
      productQuantity: parseInt(productInCart?.quantity, 10),
      handleStockError: setStockError,
    });
    setQuantityToReset(new Number(productInCart?.quantity));
    setPreviousQuantity(productInCart?.quantity);
    setActualQuantity(productInCart?.quantity);
  };

  const resetCartQuantities = () => {
    setQuantityToReset(null);
    setActualQuantity(null);
    setPreviousQuantity(0);
  };

  // discounted price data
  const [selectedDiscountedWSP, setSelectedDiscountedWSP] = useState(0);

  // description
  const [description, setDescription] = useState(addNoFollow(product?.productLongDescription));

  // Store page redirection
  const handleRedirectToBrand = () => router.push(`/store/${product?.brandSlug}`);

  const stockErrorMessage = stockError?.find((error) => {
    const itemToCheck = hasVariations ? variationIdInDisplay : cartItemId;
    return error?.productId === itemToCheck;
  });

  const currentProduct = useMemo(() => {
    if (!currency?.name) return null;
    if (!product?.variations || product?.variations?.length === 0) return product;

    let cheapest = null;

    for (const variation of product.variations) {
      const priceForCurrency = variation.attributes.productPrice.find((p) => p.productCurrency === currency.name);

      if (priceForCurrency) {
        if (!cheapest || priceForCurrency.productWSP < cheapest.wsp) {
          cheapest = {
            variation,
            wsp: priceForCurrency.productWSP,
          };
        }
      }
    }

    if (cheapest?.variation) {
      const newVariationData = {
        ...selectedVariationOptions,
      };
      const type = cheapest?.variation?.attributes?.productVariationLabels[0];
      const value = cheapest?.variation?.attributes?.productVariationValues[0];
      newVariationData[type] = value;
      setSelectedVariationOptions(newVariationData);
    }

    return cheapest?.variation
      ? {
          id: cheapest?.variation?.id,
          WP_id: cheapest?.variation?.id,
          ...cheapest?.variation?.attributes,
          inventory: { ...cheapest?.inventory },
        }
      : null;
  }, [product?.variations, currency]);

  // react to currency change
  useEffect(() => {
    if (currency?.symbol) {
      setCurrencySymbol(currency?.symbol);
    }
    if (currency?.name) {
      // WSP
      const wspInSelectedCurrency = currentProduct?.productPrice
        ?.filter((x) => x?.productWSP > 0 && x?.productCurrency === currency?.name)
        ?.pop();

      if (wspInSelectedCurrency) {
        setSelectedWSP(Number(wspInSelectedCurrency?.productWSP) / 100);
      } else {
        (async () => {
          setPriceInDesiredCurrency(
            currentProduct?.productPrice,
            'productWSP',
            'productCurrency',
            currency?.name,
            setSelectedWSP,
            exchangeRates
          );
        })();
      }

      // RRP
      const rrpInSelectedCurrency = currentProduct?.productPrice
        ?.filter((x) => x?.productRRP > 0 && x?.productCurrency === currency?.name)
        ?.pop();

      if (rrpInSelectedCurrency) {
        setSelectedRRP(Number(rrpInSelectedCurrency?.productRRP) / 100);
      } else {
        (async () => {
          setPriceInDesiredCurrency(
            currentProduct?.productPrice,
            'productRRP',
            'productCurrency',
            currency?.name,
            setSelectedRRP,
            exchangeRates
          );
        })();
      }

      // discounted price
      const discountedPriceInSelectedCurrency = currentProduct?.productDiscount
        ?.filter((x) => x?.productDiscountValue > 0 && x?.productDiscountCurrency === currency?.name)
        ?.pop();

      if (discountedPriceInSelectedCurrency) {
        setSelectedDiscountedWSP(Number(discountedPriceInSelectedCurrency.productDiscountValue / 100));
      } else {
        (async () => {
          setPriceInDesiredCurrency(
            currentProduct?.productDiscount,
            'productDiscountValue',
            'productDiscountCurrency',
            currency?.name,
            setSelectedDiscountedWSP,
            exchangeRates
          );
        })();
      }

      // min order
      const minOrderInSelectedCurrency = product?.wholesaler?.minOrderValue
        ?.filter((x) => x?.wholesalerStoreMinOrderAmount > 0 && x?.wholesalerStoreMinOrderCurrency === currency?.name)
        ?.pop();

      if (minOrderInSelectedCurrency) {
        setSelectedMinOrderValue(Number(minOrderInSelectedCurrency?.wholesalerStoreMinOrderAmount) / 100);
      } else {
        (async () => {
          setPriceInDesiredCurrency(
            product?.wholesaler?.minOrderValue,
            'wholesalerStoreMinOrderAmount',
            'wholesalerStoreMinOrderCurrency',
            currency?.name,
            setSelectedMinOrderValue,
            exchangeRates
          );
        })();
      }
    }
  }, [currency, product?.productDiscount, product?.productPrice, product?.wholesaler?.minOrderValue, exchangeRates]);

  // react to variation changes
  useEffect(() => {
    if (
      hasVariations &&
      Object.keys(selectedVariationOptions)?.length > 0 &&
      Object.keys(selectedVariationOptions)?.length === variationsMetadata?.length
    ) {
      const selectedValues = Object.values(selectedVariationOptions);

      const found = product.variations
        ?.filter((variation) =>
          variation?.attributes?.productVariationValues?.every((x) => selectedValues?.includes(x))
        )
        ?.flat(1)
        ?.pop();

      if (!found) {
        setShowVariationError(true);
        setCanAddToCart(false);
        return;
      }

      // notify variation changed
      onVariationChange(found);

      setShowVariationError(false);

      const variationIdentifier = IS_WORDPRESS_CONNECTED ? found?.WP_id : found?.id;

      setVariationIdInDisplay(variationIdentifier);

      const isOutOfStock = isProductOutOfStock({ product: found });

      if (isOutOfStock) {
        setCanAddToCart(false);
        setStockError((prev) => {
          return prev.some((p) => p?.productId === variationIdentifier)
            ? prev
            : [
                ...prev,
                {
                  productId: variationIdentifier,
                  message: 'Out of stock',
                },
              ];
        });
        return;
      }

      // prices
      // WSP
      const wspInSelectedCurrency = found.attributes?.productPrice
        ?.filter((x) => x?.productWSP > 0 && x?.productCurrency === currency?.name)
        ?.pop();

      if (wspInSelectedCurrency) {
        setSelectedWSP(Number(wspInSelectedCurrency?.productWSP) / 100);
      } else {
        (async () => {
          setPriceInDesiredCurrency(
            found?.attributes?.productPrice,
            'productWSP',
            'productCurrency',
            currency?.name,
            setSelectedWSP,
            exchangeRates
          );
        })();
      }

      // RRP
      const rrpInSelectedCurrency = found.attributes?.productPrice
        ?.filter((x) => x?.productRRP > 0 && x?.productCurrency === currency?.name)
        ?.pop();

      if (rrpInSelectedCurrency) {
        setSelectedRRP(Number(rrpInSelectedCurrency?.productRRP) / 100);
      } else {
        (async () => {
          setPriceInDesiredCurrency(
            found?.attributes?.productPrice,
            'productRRP',
            'productCurrency',
            currency?.name,
            setSelectedRRP,
            exchangeRates
          );
        })();
      }

      // discounted price
      const discountedPriceInSelectedCurrency = found?.attributes?.productDiscount
        ?.filter((x) => x?.productDiscountValue > 0 && x?.productDiscountCurrency === currency?.name)
        ?.pop();

      if (discountedPriceInSelectedCurrency) {
        setSelectedDiscountedWSP(Number(discountedPriceInSelectedCurrency.productDiscountValue / 100));
      } else {
        (async () => {
          setSelectedDiscountedWSP(null);
          setPriceInDesiredCurrency(
            found?.attributes?.productDiscount,
            'productDiscountValue',
            'productDiscountCurrency',
            currency?.name,
            setSelectedDiscountedWSP,
            exchangeRates
          );
        })();
      }

      // description
      const variationDescription = found?.attributes?.productLongDescription;
      if (variationDescription) {
        setDescription(addNoFollow(variationDescription));
      }

      // cart data
      setCartItemId(variationIdentifier);

      const foundInCart = cart
        ?.filter((x) => x?.productId === productIdentifier && x?.variationId === variationIdentifier)
        ?.pop();

      if (foundInCart && Object.keys(foundInCart)?.length > 0) {
        setInCart(true);
        setCanAddToCart(false);
        setProductInCartQuantities(foundInCart);
        validateStockAmount({
          product,
          cartItemId: variationIdentifier,
          hasVariations: true,
          productQuantity: foundInCart?.quantity,
          handleStockError: setStockError,
        });
      } else {
        resetCartQuantities();
        setInCart(false);
        setCanAddToCart(true);

        const minQuantity = found?.attributes?.productMinQuantity > 0 ? found?.attributes?.productMinQuantity : 1;
        setAddToCartMinQuantity(minQuantity);
      }
    }
  }, [
    cart,
    currency?.name,
    hasVariations,
    onVariationChange,
    productIdentifier,
    product?.variations,
    selectedVariationOptions,
    variationsMetadata,
    IS_WORDPRESS_CONNECTED,
    exchangeRates,
  ]);

  const handleVariationSelect = (event) => {
    // handle UI selects
    // event.target.value === type#attribute
    const [type, value] = event.target.value.split('#');

    const newVariationData = {
      ...selectedVariationOptions,
    };
    newVariationData[type] = value;

    setSelectedVariationOptions(newVariationData);
  };

  // initial setup
  useEffect(() => {
    // description
    setDescription(addNoFollow(product?.productLongDescription));

    const mHasVariations = product?.variations && product?.variations?.length > 0;
    setHasVariations(mHasVariations);

    // variation data
    if (hasVariations) {
      setVariationsMetadata(product?.variationsMetadata);
      setCanAddToCart(false);
    } else {
      const minQuantity = product?.productMinQuantity > 0 ? product?.productMinQuantity : 1;
      setAddToCartMinQuantity(minQuantity);
      setCartItemId(productIdentifier);
      setCanAddToCart(true);
    }
  }, [product, hasVariations, productIdentifier]);

  // react to cart updates
  useEffect(() => {
    if (cart) {
      if (hasVariations) {
        const variationInCart = cart
          ?.filter((x) => x?.productId === productIdentifier && x?.variationId === variationIdInDisplay)
          ?.pop();

        // variation in display?
        if (variationInCart && Object.keys(variationInCart)?.length > 0) {
          setCartItemId(variationInCart?.variationId);
        } else {
          resetCartQuantities();
          setInCart(false);
        }
      } else {
        const productInCart = cart?.filter((x) => x?.productId === productIdentifier)?.pop();

        if (productInCart && Object.keys(productInCart)?.length > 0) {
          setProductInCartQuantities(productInCart);
          setInCart(true);
          setCartItemId(productInCart?.productId);
        } else {
          resetCartQuantities();
          setInCart(false);
        }
      }
    }
  }, [cart, productIdentifier, product?.variations, hasVariations, variationIdInDisplay, IS_WORDPRESS_CONNECTED]);

  // this function will be triggered only if product is not added to cart yet
  const handleOnAddToCart = async () => {
    setIsAddingToCart(true);

    try {
      // passing product and selectedProductId because for variation product need the details of parent product
      await addToCart({
        product,
        quantity: addToCartMinQuantity,
        selectedProductId: cartItemId,
        queryId: algoliaQueryParam,
      });
      GTMProductAddToCartEvents(
        product?.productName,
        product?.WP_id || productIdentifier,
        selectedWSP,
        product?.wholesaler?.name,
        `/product/${product?.productSlug}`,
        `${product?.productThumbnailUrl?.productThumbnailBaseUrl}/${product?.productThumbnailUrl?.productThumbnailName}`,
        currency?.name,
        userId,
        user?.retailerOrderCount
      );

      if (shouldSendAlgoliaInsights) {
        const eventData = {
          objectID: product?.id,
          ...(algoliaQueryParam && { queryID: algoliaQueryParam }),
        };
        pushAlgoliaAddToCartEvent(eventData, hasAlgoliaQueryParam);
      }

      setPreviousQuantity(addToCartMinQuantity);
    } catch (error) {
      // error was already handled in cart provider
      // no UI resets needed
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleRemoveFromCart = async () => {
    try {
      await removeFromCart({ itemId: cartItemId });
      // reset quantities
      resetCartQuantities();
    } catch (error) {
      // reset quantity selector to previous state
      setQuantityToReset(new Number(previousQuantity));
    }
  };

  const handleIncreaseInCart = async (actual, previous) => {
    try {
      const quantity = actual - previous;
      await increaseInCart({ itemId: cartItemId, quantity });

      setPreviousQuantity(actual);
    } catch (error) {
      // reset quantity selector to previous state
      setQuantityToReset(new Number(previousQuantity));
    }
  };

  const handleDecreaseInCart = async (actual, previous) => {
    try {
      const quantity = previous - actual;

      await decreaseInCart({ itemId: cartItemId, quantity });

      setPreviousQuantity(actual);
    } catch (error) {
      // reset quantity selector to previous state
      setQuantityToReset(new Number(previousQuantity));
    }
  };

  const handleOnClickQuantitySelector = async ({ previous, actual }) => {
    if (shouldTriggerCartApi) {
      setIsSelectingQuantity(true);

      if (Number.isInteger(actual) && Number.isInteger(previous) && actual !== previous) {
        if (actual < addToCartMinQuantity) {
          await handleRemoveFromCart();
        } else if (actual > previous) {
          await handleIncreaseInCart(actual, previous);
        } else if (previous > actual) {
          await handleDecreaseInCart(actual, previous);
        }
      }

      setIsSelectingQuantity(false);
      setShouldTriggerCartApi(false);
    }
  };

  const quantitySelectorHelper = ({ actual }) => {
    validateStockAmount({
      product,
      cartItemId,
      hasVariations,
      productQuantity: actual,
      handleStockError: setStockError,
    });
    setActualQuantity(actual);
    setShouldTriggerCartApi(true);
  };

  useDebounce(async () => handleOnClickQuantitySelector({ actual: actualQuantity, previous: previousQuantity }), 400, [
    actualQuantity,
  ]);

  useLayoutEffect(() => {
    if (isMobile) {
      setExpanded('');
    }
  }, [isMobile]);

  // Tooltip when text is selected
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipContent = (
    <>
      <Stack direction="row" spacing={0.5}>
        <PaymentsOutlined color="white" />
        <Typography variant="subtitle1" fontWeight="bold" color="white">
          Best Price Guarantee
        </Typography>
      </Stack>
      <Typography variant="body1" color="inherit">
        If you find this product cheaper within 30 days of ordering, we&apos;ll refund you the difference.{' '}
        <Link
          underline="always"
          href="https://wholesale.creoate.com/price-match-promise"
          target="_blank"
          rel="noopener"
          color="#ffffff"
        >
          Find out more.
        </Link>
      </Typography>
    </>
  );

  const handleMouseDown = () => {
    setShowTooltip(false);
  };

  const handleMouseUp = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString();

    if (selectedText !== '') {
      setShowTooltip(true);
    }
  };

  const handleDocumentClick = (event) => {
    const textElement = event.target.closest('.text-container');

    if (!textElement || !textElement?.contains(event.target)) {
      setShowTooltip(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  // UI elements
  let productPrice;
  if (selectedDiscountedWSP) {
    productPrice = selectedWSP && (
      <Stack
        spacing={{ xs: 0, sm: 2 }}
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'left', sm: 'center' }}
      >
        <Typography color="error" className="singlePagePriceTag" variant="body1" fontSize={24} align="left">
          <span className="notranslate">
            WSP {currencySymbol}
            {splitPrice(selectedDiscountedWSP)?.price}.
            <span className="priceCent">{splitPrice(selectedDiscountedWSP)?.floating}</span>
          </span>
        </Typography>
        <Typography color="textPrimary" className="priceTag" sx={styles.strike}>
          <span className="notranslate">
            {currencySymbol}
            {splitPrice(selectedWSP)?.price}.<span className="priceCent">{splitPrice(selectedWSP)?.floating}</span>
          </span>
        </Typography>
      </Stack>
    );
  } else {
    productPrice = selectedWSP && (
      <Typography variant="body1" className="singlePagePriceTag" fontSize={24} align="left">
        <span className="notranslate">
          WSP {currencySymbol}
          {splitPrice(selectedWSP)?.price}.<span className="priceCent">{splitPrice(selectedWSP)?.floating}</span>
        </span>
      </Typography>
    );
  }

  const creditInfoText = isMobile ? (
    <div>
      PAY 60 DAYS LATER, INTEREST FREE.
      <br />
      CHOOSE ‘PAY LATER’ AT CHECKOUT.
    </div>
  ) : (
    `PAY 60 DAYS LATER, INTEREST FREE. CHOOSE ‘PAY LATER’ AT CHECKOUT.`
  );

  const handleFollowButtonClick = async () => {
    if (!isAuthenticated) {
      handleShowSignupPopup();
      return;
    }

    if (isFollowing) {
      await unfollow(product?.wholesaler?.wpId);
    } else {
      await follow(product?.wholesaler?.wpId);
    }
  };

  const handleOnMouseEnter = () => {
    setButtonText(isFollowing ? BUTTON_TEXT_VALUES.UNFOLLOW : BUTTON_TEXT_VALUES.FOLLOW);
  };

  const handleOnMouseLeave = () => {
    if (isFollowing) {
      setButtonText(BUTTON_TEXT_VALUES.FOLLOWING);
    } else {
      setButtonText(BUTTON_TEXT_VALUES.FOLLOW);
    }
  };

  const buttonStyles = isFollowing
    ? {
        variant: 'contained',
        color: 'common.white',
      }
    : {
        variant: 'contained',
        color: 'primary',
      };

  useEffect(() => {
    const checkIfIsFollowing = following?.includes(product?.wholesaler?.wpId);
    setIsFollowing(checkIfIsFollowing);
    if (checkIfIsFollowing) {
      setButtonText(BUTTON_TEXT_VALUES.FOLLOWING);
    } else {
      setButtonText(BUTTON_TEXT_VALUES.FOLLOW);
    }
  }, [following, product?.wholesaler?.wpId]);

  return (
    <Box sx={{ ...sx }}>
      <Box sx={{ mb: 1.75, [theme?.breakpoints?.down('md')]: { mb: 1.125, mt: 1.25 } }}>
        {product?.flair?.value &&
          (loading ? (
            <Skeleton width={100} height={32} />
          ) : (
            <Label
              variant="filled"
              type={product?.flair?.type}
              sx={{
                position: 'flex',
                textTransform: 'uppercase',
              }}
            >
              {product?.flair?.value}
            </Label>
          ))}
        {loading ? (
          <Skeleton width={350} height={32} />
        ) : (
          <>
            <Typography component="h1" variant="h5" align="left" data-testid="product-detail-name">
              <div
                className="text-container"
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                style={{ userSelect: 'text' }}
                role="textbox"
                tabIndex={0}
                title={showTooltip ? tooltipContent : ''}
              >
                {decode(product?.productName)}
              </div>
            </Typography>
            {showTooltip && (
              <Tooltip title={tooltipContent} placement="bottom-start" open={showTooltip}>
                <span />
              </Tooltip>
            )}
          </>
        )}
      </Box>
      {/* wholesaler info - show to all users */}
      <Box
        sx={{
          mb: 1.5,
          display: 'flex',
          justifyContent: 'left',
          alignItems: 'center',
          [theme?.breakpoints?.down('md')]: { mb: 1 },
        }}
      >
        <Box>
          {loading ? (
            <Stack direction="row" spacing={5} alignItems="center" justifyContent="space-between">
              <CardHeader
                sx={styles.brandArea}
                avatar={<Skeleton animation="wave" variant="circular" width={40} height={40} sx={{ mb: 1 }} />}
                title={<Skeleton animation="wave" variant="text" width={100} height={30} />}
                subheader={<Skeleton animation="wave" variant="text" width={150} height={30} sx={{ mb: 2 }} />}
              />
              <Skeleton animation="wave" variant="rounded" width={50} height={30} />
            </Stack>
          ) : (
            <Stack direction="row" spacing={5} alignItems="center" justifyContent="space-between">
              <CardActionArea onClick={handleRedirectToBrand}>
                <CardHeader
                  sx={styles.brandArea}
                  data-testid="product-card-header"
                  avatar={
                    <Avatar
                      alt={product?.wholesaler?.name}
                      sx={styles.avatar}
                      aria-label={product?.wholesaler?.name}
                      src={transformImageUrl(
                        product?.wholesaler?.logo?.wholesalerStoreLogoBasePath,
                        product?.wholesaler?.logo?.wholesalerStoreLogoImage,
                        isMobile ? RESOLUTION.LOGO.MOBILE : RESOLUTION.LOGO.DESKTOP,
                        true
                      )}
                    />
                  }
                  subheader={`${product?.wholesaler?.country}, Minimum ${currencySymbol}${selectedMinOrderValue}`}
                  title={<span className="notranslate">{product?.wholesaler?.name}</span>}
                />
              </CardActionArea>
              <Button
                {...buttonStyles}
                data-testid="brand-card-follow-button"
                onClick={handleFollowButtonClick}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
                size="small"
                sx={{
                  padding: `${following ? '0.2rem 1.5rem' : '0.2rem 1rem'}`,
                  mt: `0 !important`,
                }}
              >
                {buttonText}
              </Button>
            </Stack>
          )}
        </Box>
      </Box>
      {/* Prices */}
      <Box display="flex" flexDirection="column" mb={0.5}>
        {isAuthenticated && (loading ? <Skeleton width={150} height={40} /> : productPrice)}

        {loading ? (
          <Skeleton width={isAuthenticated ? 90 : 150} height={isAuthenticated ? 26 : 40} />
        ) : (
          <Typography
            variant="body1"
            fontSize={isAuthenticated ? 16 : 24}
            align="left"
            className={isAuthenticated ? 'priceTag' : 'singlePagePriceTag'}
            sx={{ color: isAuthenticated ? 'grey.500' : '' }}
            data-testid="product-detail-rrp"
          >
            <span className="notranslate">
              RRP {currencySymbol}
              {splitPrice(selectedRRP)?.price}.<span className="priceCent">{splitPrice(selectedRRP)?.floating}</span>
            </span>
          </Typography>
        )}
      </Box>
      {isAuthenticated && (
        <>
          {/* Variations dropdown select */}
          {loading ? (
            <Skeleton width={280} height={68} />
          ) : (
            hasVariations &&
            variationsMetadata?.map(({ type, values }) => (
              <Paper
                elevation={0}
                sx={{
                  width: 280,
                  mt: 0.75,
                  [theme.breakpoints.down('md')]: {
                    width: 'initial',
                  },
                }}
                key={type}
              >
                <FormControl fullWidth>
                  <InputLabel
                    id="attribute-select-label"
                    variant="standard"
                    data-testid="variation-options-select"
                    sx={{ marginLeft: '1rem' }}
                  >{`Select ${type}`}</InputLabel>
                  <Select
                    native
                    id="attribute-select"
                    data-testid="add-to-cart-variation-select"
                    value={selectedVariationOptions[type] ? `${type}#${selectedVariationOptions[type]}` : null}
                    labelId="attribute-select-label"
                    label={`Select ${type}`}
                    onChange={handleVariationSelect}
                  >
                    {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                    <option key={type} value="" selected disabled hidden />
                    {values.map((attribute) => (
                      <option
                        data-testid="add-to-cart-variation-select-items"
                        key={`${type}-${attribute}`}
                        value={`${type}#${attribute}`}
                      >
                        {attribute}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            ))
          )}

          {/* out of stock / stock quantity message */}
          {stockErrorMessage && (
            <Typography variant="body2" color="#d32f2f" marginTop={1}>
              {stockErrorMessage?.message}
            </Typography>
          )}

          {/* Add to cart button / quantity increase, decrease  */}
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              [theme.breakpoints.down('md')]: {
                width: 'initial',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {loading && <Skeleton width={165} height={42} />}
              {!loading && inCart && (
                <>
                  <Typography variant="body1">Added to your cart</Typography>
                  <QuantitySelector
                    initialValue={minimumOrderQuantity}
                    minimumValue={minimumOrderQuantity}
                    maximumValue={maxStockAvailable}
                    multipleOf={productOrderQuantity}
                    isDisabled={isSelectingQuantity}
                    quantityToReset={quantityToReset}
                    onChange={quantitySelectorHelper}
                    data-testid="quantity-selector"
                  />
                </>
              )}
              {!loading && !inCart && (
                <Button
                  size="large"
                  type="button"
                  color="primary"
                  variant="contained"
                  data-testid="add-to-cart-button"
                  onClick={handleOnAddToCart}
                  disabled={isAddingToCart || !canAddToCart}
                  fullWidth={isTablet}
                >
                  Add to basket
                </Button>
              )}
            </Box>
            {productOrderQuantity > 1 && (
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                data-testid="product-increment-message"
                sx={{ justifyContent: 'center' }}
              >
                Sold in increments of {productOrderQuantity}
              </Typography>
            )}
            {showVariationError && (
              <Box display="flex" alignItems="center">
                <InfoTwoToneIcon />
                <Typography variant="body1" color="initial" sx={{ marginLeft: '4px' }}>
                  <b>Unavailable</b> Currently do not have products matching your selection
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}
      <Box
        sx={{
          backgroundColor: '#F8FAFA',
          borderRadius: '0.6rem',
          py: '1rem',
          px: '1.55rem',
          [theme.breakpoints.down('md')]: {
            px: '1rem',
          },
          m: '1rem 0 0.5rem',
        }}
      >
        {/* Lead time / Shipping time */}
        {loading && <Skeleton width="75%" height={24} />}
        {!loading && leadTimeText && (
          <ActionInfoButton
            marginTop={0}
            Icon={DateRangeIcon}
            message={leadTimeText}
            tooltipContent={{
              text: 'This is an estimated delivery window, based on the brand’s average dispatch time and typical courier delivery times. It may vary — we’ll keep you updated along the way.',
            }}
            dataTestId="lead-time-info-button"
          />
        )}

        {/* Price guarantee */}
        {loading ? (
          <Skeleton width="75%" height={24} />
        ) : (
          <ActionInfoButton
            Icon={GppGoodOutlinedIcon}
            message="<b>Price Match</b> If you find a lower price, we'll match it."
            tooltipContent={{
              text: "If you find this product cheaper within 30 days of ordering, we'll refund you the difference.",
              link: {
                text: 'Find out more',
                href: 'https://wholesale.creoate.com/price-match-promise',
              },
            }}
          />
        )}

        {/* Free Return */}
        {loading ? (
          <Skeleton width="75%" height={24} />
        ) : (
          <ActionInfoButton
            Icon={StorefrontIcon}
            message="New to this brand? <b>Free-return</b> within 60-days, no hassle."
            tooltipContent={{
              text: 'Take the risk out of trying new products and unsold items. Our risk-free 60-day returns policy allows you to order items, try them out, and return what doesn’t sell or you don’t want to keep.',
              link: {
                text: 'Learn more',
                href: 'https://www.creoate.com/returns-policy',
              },
            }}
          />
        )}

        {/* Free Shipping */}
        {['GB', 'US'].includes(user?.shippingCountry) &&
          (loading ? (
            <Skeleton width="75%" height={24} />
          ) : (
            <ActionInfoButton
              Icon={PackageIcon}
              message={`Free shipping over <b>${user.shippingCountry === 'GB' ? '£300' : '$390'}</b>`}
              dataTestId="free-shipping-info-button"
            />
          ))}
      </Box>
      {isAuthenticated ? (
        <Box>
          {/* Credit info message */}
          {loading ? (
            <Skeleton animation="wave" variant="text" width="100%" height={20} />
          ) : (
            renderKycInfo(user, cookies?.[USER_CREDIT_COOKIE_NAME]) && (
              <Label
                variant="filled"
                type="NEW"
                sx={{
                  padding: '4px, 10px, 4px, 10px',
                  ...(isMobile ? { WebkitLineClamp: '2', lineHeight: 'unset', height: '44px' } : {}),
                }}
              >
                {creditInfoText}
              </Label>
            )
          )}
        </Box>
      ) : (
        <Paper
          sx={{
            backgroundColor: 'grey.300',
            border: 'solid 1px rgba(0, 0, 0, 0.12)',
            [theme.breakpoints.up('md')]: {
              mt: '75px',
            },
          }}
        >
          <Box display="flex" flexDirection="column">
            <Box display="flex" justifyContent="space-around" alignItems="center" mt={2}>
              <Box display="flex" justifyContent="center" alignItems="center">
                {loading ? (
                  <Skeleton animation="wave" variant="text" width={100} height={20} />
                ) : (
                  <>
                    <LocalOfferIcon sx={{ width: 18, height: 18, color: 'text.secondary' }} />
                    <Typography fontSize={16} ml={0.5}>
                      See Wholesale price
                    </Typography>
                  </>
                )}
              </Box>

              <Box display="flex" justifyContent="center" alignItems="center">
                {loading ? (
                  <Skeleton animation="wave" variant="text" width={100} height={20} />
                ) : (
                  <>
                    <LocalPostOffice sx={{ width: 18, height: 18, color: 'text.secondary' }} />
                    <Typography fontSize={16} ml={0.5}>
                      Message supplier
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
            <Box display="flex" justifyContent="center" mt={1.5} mb={2}>
              {loading ? (
                <Skeleton animation="wave" variant="rounded" width={250} height={50} />
              ) : (
                <Button
                  size="large"
                  color="primary"
                  variant="contained"
                  sx={{ width: 250 }}
                  onClick={handleOnSignupClick}
                >
                  Register to see price
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      )}
      {/* Description accordion */}
      {loading ? (
        <Skeleton animation="wave" variant="rounded" width="100%" height={50} sx={{ mt: 2 }} />
      ) : (
        <Accordion
          sx={{ mt: 2 }}
          expanded={expanded === ACCORDION_PANELS.DESCRIPTION}
          onChange={handlePanelChange(ACCORDION_PANELS.DESCRIPTION)}
        >
          <AccordionSummary
            id="panel1a-header"
            aria-controls="panel1a-content"
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: { xs: 1, md: 2 }, '& .MuiAccordionSummary-content': { overflow: 'hidden' } }}
          >
            <Typography sx={{ width: { xs: '45%', md: '33%' }, flexShrink: 0, textTransform: 'uppercase' }}>
              Description
            </Typography>
            <Typography
              sx={{
                color: 'text.secondary',
                [theme.breakpoints.down('md')]: {
                  ml: 'auto',
                  pr: 0.25,
                },
              }}
              noWrap
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </AccordionSummary>
          <AccordionDetails>
            <Typography dangerouslySetInnerHTML={{ __html: description }} whiteSpace="pre-line" />
          </AccordionDetails>
        </Accordion>
      )}
      {/* Vendor info accordion */}
      <Card
        style={{
          padding: '1rem',
          boxShadow: 'none',
          backgroundColor: '#FFF8F4',
          marginTop: '1.5rem',
          marginBottom: '1.5rem',
        }}
        data-test="card-vendor-info"
      >
        <Stack direction="row" alignItems="center" spacing={2} textAlign="left" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            {loading ? (
              <Skeleton animation="wave" variant="circular" width={40} height={40} />
            ) : (
              <Avatar
                alt={product?.wholesaler?.name}
                sx={{ height: '40px', width: '40px' }}
                aria-label={product?.wholesaler?.name}
                src={transformImageUrl(
                  product?.wholesaler?.logo?.wholesalerStoreLogoBasePath,
                  product?.wholesaler?.logo?.wholesalerStoreLogoImage,
                  isMobile ? RESOLUTION.LOGO.MOBILE : RESOLUTION.LOGO.DESKTOP,
                  true
                )}
              />
            )}
            <Stack direction="column">
              {loading ? (
                <>
                  <Skeleton animation="wave" variant="text" width={100} height={20} />
                  <Skeleton animation="wave" variant="text" width={70} height={20} />
                  <Skeleton animation="wave" variant="text" width={70} height={20} />
                </>
              ) : (
                <>
                  <Typography variant={`${isMobile ? 'subtitle1' : 'body1'}`}>
                    <Link href={`/store/${product?.wholesaler?.slug}`} passHref legacyBehavior>
                      <MuiLink
                        sx={{ color: 'inherit' }}
                        className="notranslate"
                        data-test="card-vendor-info-lbl-wholesaler-name"
                      >
                        {product?.wholesaler?.name}
                      </MuiLink>
                    </Link>
                  </Typography>
                  <Typography variant="body2" data-test="card-vendor-info-lbl-wholesaler-country">
                    {product?.wholesaler?.country}
                  </Typography>
                  <Typography variant="body2" data-test="card-vendor-info-lbl-wholesaler-minimum">
                    Minimum {currencySymbol} {selectedMinOrderValue}
                  </Typography>
                </>
              )}
            </Stack>
          </Stack>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 0.5,
              alignItems: isMobile ? 'flex-end' : 'center',
              ...(isMobile && { width: '100%' }),
            }}
          >
            {loading ? (
              <>
                <Skeleton animation="wave" variant="rounded" width={90} height={30} />
                <Skeleton animation="wave" variant="rounded" width={90} height={30} />
              </>
            ) : (
              <>
                <Button
                  {...buttonStyles}
                  data-testid="card-vendor-info-btn-follow"
                  onClick={handleFollowButtonClick}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                  size="small"
                  sx={{ padding: `${following ? '0.2rem 1.5rem' : '0.2rem 1rem'}` }}
                >
                  {buttonText}
                </Button>
                <CustomTooltip
                  title={
                    <Typography>
                      We’re sorry, messaging is temporarily unavailable while we work on improvements. If you’ve already
                      placed an order with this seller, you can still message them from your <strong>Dashboard.</strong>{' '}
                      Thanks for your patience!
                    </Typography>
                  }
                >
                  <Button
                    data-testid="card-vendor-info-btn-msg-seller"
                    onClick={() => {
                      setMessageBrandDialogOpen(true);
                    }}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{
                      padding: '0.2rem 1rem',
                      backgroundColor: 'white',
                    }}
                  >
                    MESSAGE SELLER
                  </Button>
                </CustomTooltip>
                <MessageBrandDialog
                  brandData={{
                    name: product?.wholesaler?.name,
                    wpId: product?.wholesaler?.wpId,
                  }}
                  messageDialogOpen={messageBrandDialogOpen}
                  handleMessageDialogClose={() => {
                    setMessageBrandDialogOpen(false);
                  }}
                />
              </>
            )}
          </Box>
        </Stack>
      </Card>
    </Box>
  );
};

ProductDetailInfo.propTypes = {
  sx: PropTypes.shape({}),
  loading: PropTypes.bool.isRequired,
  product: PropTypes.shape({
    id: PropTypes.string,
    WP_id: PropTypes.string,
    shipsIn: PropTypes.string,
    productName: PropTypes.string,
    productMinQuantity: PropTypes.number,
    productLongDescription: PropTypes.string,
    brandSlug: PropTypes.string.isRequired,
    productSlug: PropTypes.string,
    productThumbnailUrl: PropTypes.shape({
      productThumbnailName: PropTypes.string,
      productThumbnailBaseUrl: PropTypes.string,
    }),
    flair: PropTypes.oneOfType([
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }),
      PropTypes.string,
    ]),
    productPrice: PropTypes.arrayOf(
      PropTypes.shape({
        productRRP: PropTypes.number,
        productWSP: PropTypes.number,
        productCurrency: PropTypes.string,
      })
    ),
    productDiscount: PropTypes.arrayOf(
      PropTypes.shape({
        productDiscountValue: PropTypes.number,
        productDiscountCurrency: PropTypes.string,
      })
    ),
    wholesaler: PropTypes.shape({
      name: PropTypes.string,
      wpId: PropTypes.string,
      slug: PropTypes.string,
      description: PropTypes.string,
      country: PropTypes.string,
      logo: PropTypes.shape({
        wholesalerStoreLogoImage: PropTypes.string,
        wholesalerStoreLogoBasePath: PropTypes.string,
      }),
      minOrderValue: PropTypes.arrayOf(
        PropTypes.shape({
          wholesalerStoreMinOrderAmount: PropTypes.number,
          wholesalerStoreMinOrderCurrency: PropTypes.string,
        })
      ),
      roundedAverageLeadTime: PropTypes.number,
    }),
    variations: PropTypes.arrayOf(
      PropTypes.shape({
        WP_id: PropTypes.string,
        attributes: PropTypes.arrayOf(
          PropTypes.shape({
            productVariationLabels: PropTypes.arrayOf(PropTypes.string),
            productVariationValues: PropTypes.arrayOf(PropTypes.string),
            productMinQuantity: PropTypes.number,
            productLongDescription: PropTypes.string,
            productThumbnailUrl: PropTypes.shape({
              productThumbnailName: PropTypes.string,
              productThumbnailBaseUrl: PropTypes.string,
            }),
            productPrice: PropTypes.arrayOf(
              PropTypes.shape({
                productRRP: PropTypes.number,
                productWSP: PropTypes.number,
                productCurrency: PropTypes.string,
              })
            ),
            productDiscount: PropTypes.arrayOf(
              PropTypes.shape({
                productDiscountValue: PropTypes.number,
                productDiscountCurrency: PropTypes.string,
              })
            ),
          })
        ),
      })
    ),
    variationsMetadata: PropTypes.shape({
      type: PropTypes.string,
      values: PropTypes.arrayOf(PropTypes.string),
    }),
    productInventory: PropTypes.shape({
      productStock: PropTypes.number,
      productInventoryTracked: PropTypes.string,
    }),
    productOrderQuantity: PropTypes.number,
  }).isRequired,
  onVariationChange: PropTypes.func.isRequired,
};

export default ProductDetailInfo;
